import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Plus, 
  Edit, 
  Trash2,
  Loader2, 
  ClipboardList,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  FileSignature,
  Info
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// US States for contract pattern state selection
const US_STATES = [
  { id: "TN", name: "Tennessee" },
  { id: "NC", name: "North Carolina" },
  { id: "IA", name: "Iowa" },
  { id: "AL", name: "Alabama" },
  { id: "GA", name: "Georgia" },
  { id: "KY", name: "Kentucky" },
  { id: "VA", name: "Virginia" },
];

const EnrollmentTypesPage = () => {
  const { user } = useAuth();
  const [enrollmentTypes, setEnrollmentTypes] = useState([]);
  const [fundingSources, setFundingSources] = useState([]);
  const [contractTypeTags, setContractTypeTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [deletingType, setDeletingType] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Check if user can configure contract patterns (System Admin or Executive only)
  const canConfigureContractPattern = user?.role === 'system_admin' || user?.role === 'executive';
  
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
    default_contract_type: '',
    default_funding_source: '',
    allowed_funding_sources: [],
    duration_months: 12,
    is_active: true,
    // New: Default contract pattern for auto-resolving active contracts
    default_contract_pattern: {
      contract_type_tag: '',
      state: ''
    }
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [etResponse, fsResponse] = await Promise.all([
        axios.get(`${API}/admin/enrollment-types`),
        axios.get(`${API}/admin/funding-sources`)
      ]);
      setEnrollmentTypes(etResponse.data.enrollment_types || []);
      setFundingSources(fsResponse.data.funding_sources || []);
      
      // Fetch contract type tags if user can configure patterns
      if (canConfigureContractPattern) {
        try {
          const tagsResponse = await axios.get(`${API}/admin/contract-type-tags`);
          setContractTypeTags(tagsResponse.data.tags || []);
        } catch (err) {
          console.error('Failed to load contract type tags:', err);
        }
      }
    } catch (error) {
      toast.error('Failed to load enrollment types');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (type = null) => {
    if (type) {
      setEditingType(type);
      setFormData({
        id: type.id,
        name: type.name,
        description: type.description || '',
        default_contract_type: type.default_contract_type || '',
        default_funding_source: type.default_funding_source || '',
        allowed_funding_sources: type.allowed_funding_sources || [],
        duration_months: type.duration_months || 12,
        is_active: type.is_active !== false,
        default_contract_pattern: type.default_contract_pattern || {
          contract_type_tag: '',
          state: ''
        }
      });
    } else {
      setEditingType(null);
      setFormData({
        id: '',
        name: '',
        description: '',
        default_contract_type: '',
        default_funding_source: '',
        allowed_funding_sources: [],
        duration_months: 12,
        is_active: true,
        default_contract_pattern: {
          contract_type_tag: '',
          state: ''
        }
      });
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingType) {
        await axios.put(`${API}/admin/enrollment-types/${editingType.id}`, formData);
        toast.success('Enrollment type updated successfully');
      } else {
        const typeId = formData.id || formData.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_&]/g, '');
        await axios.post(`${API}/admin/enrollment-types`, {
          ...formData,
          id: typeId
        });
        toast.success('Enrollment type created successfully');
      }
      setShowDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save enrollment type');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingType) return;
    setSaving(true);

    try {
      await axios.delete(`${API}/admin/enrollment-types/${deletingType.id}`);
      toast.success('Enrollment type deleted successfully');
      setShowDeleteDialog(false);
      setDeletingType(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete enrollment type');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (type) => {
    try {
      await axios.put(`${API}/admin/enrollment-types/${type.id}`, {
        is_active: !type.is_active
      });
      toast.success(`Enrollment type ${!type.is_active ? 'activated' : 'deactivated'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update enrollment type');
    }
  };

  const handleFundingSourceToggle = (sourceId) => {
    const current = formData.allowed_funding_sources || [];
    if (current.includes(sourceId)) {
      setFormData({
        ...formData,
        allowed_funding_sources: current.filter(id => id !== sourceId)
      });
    } else {
      setFormData({
        ...formData,
        allowed_funding_sources: [...current, sourceId]
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="enrollment-types-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="enrollment-types-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Enrollment Types</h1>
          <p className="text-gray-500">Configure program enrollment types and their default settings</p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-enrollment-type-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Enrollment Type
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Enrollment Types ({enrollmentTypes.length})
          </CardTitle>
          <CardDescription>
            Manage enrollment types that determine program enrollment defaults
          </CardDescription>
        </CardHeader>
        <CardContent>
          {enrollmentTypes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No enrollment types configured</p>
              <p className="text-sm">Click &quot;Add Enrollment Type&quot; to create one</p>
            </div>
          ) : (
            <div className="divide-y">
              {enrollmentTypes.map((type) => (
                <div 
                  key={type.id} 
                  className="py-4 flex items-center justify-between"
                  data-testid={`enrollment-type-row-${type.id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{type.name}</span>
                      <Badge variant={type.is_active ? "default" : "secondary"}>
                        {type.is_active ? (
                          <><CheckCircle className="w-3 h-3 mr-1" />Active</>
                        ) : (
                          <><XCircle className="w-3 h-3 mr-1" />Inactive</>
                        )}
                      </Badge>
                    </div>
                    {type.description && (
                      <p className="text-sm text-gray-500 mt-1">{type.description}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                      <span>Duration: {type.duration_months || 12} months</span>
                      {type.default_contract_pattern?.contract_type_tag && (
                        <span className="flex items-center gap-1">
                          <FileSignature className="w-3 h-3" />
                          Contract: {type.default_contract_pattern.contract_type_tag}
                          {type.default_contract_pattern.state && ` (${type.default_contract_pattern.state})`}
                        </span>
                      )}
                      {type.default_funding_source && (
                        <span>Default Funding: {
                          fundingSources.find(fs => fs.id === type.default_funding_source)?.name || type.default_funding_source
                        }</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleToggleActive(type)}
                      data-testid={`toggle-enrollment-type-${type.id}`}
                    >
                      {type.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleOpenDialog(type)}
                      data-testid={`edit-enrollment-type-${type.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => {
                        setDeletingType(type);
                        setShowDeleteDialog(true);
                      }}
                      data-testid={`delete-enrollment-type-${type.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingType ? 'Edit Enrollment Type' : 'Add Enrollment Type'}</DialogTitle>
            <DialogDescription>
              Configure enrollment type settings and defaults
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., SNAP E&T"
                required
                data-testid="enrollment-type-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description of this enrollment type"
                rows={2}
                data-testid="enrollment-type-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="duration">Duration (months)</Label>
                <Input
                  id="duration"
                  type="number"
                  min="1"
                  max="120"
                  value={formData.duration_months}
                  onChange={(e) => setFormData({...formData, duration_months: parseInt(e.target.value) || 12})}
                  data-testid="enrollment-type-duration-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="default_funding">Default Funding Source</Label>
                <Select
                  value={formData.default_funding_source || "none"}
                  onValueChange={(value) => setFormData({...formData, default_funding_source: value === "none" ? "" : value})}
                >
                  <SelectTrigger data-testid="enrollment-type-funding-select">
                    <SelectValue placeholder="Select default funding" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {fundingSources.filter(fs => fs.is_active).map((fs) => (
                      <SelectItem key={fs.id} value={fs.id}>{fs.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Default Contract Pattern - Only visible to System Admin and Executive */}
            {canConfigureContractPattern && (
              <div className="space-y-3 p-4 border rounded-lg bg-blue-50/50 border-blue-200">
                <div className="flex items-center gap-2">
                  <FileSignature className="w-4 h-4 text-blue-600" />
                  <Label className="text-sm font-medium text-blue-900">Default Contract Pattern</Label>
                </div>
                <p className="text-xs text-blue-700 flex items-start gap-1">
                  <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  Configure the contract pattern to auto-default the active contract when this enrollment type is selected. 
                  The system will find the currently active contract matching this pattern based on effective dates.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contract_type_tag" className="text-xs">Contract Type Tag</Label>
                    <Select
                      value={formData.default_contract_pattern?.contract_type_tag || "none"}
                      onValueChange={(value) => setFormData({
                        ...formData, 
                        default_contract_pattern: {
                          ...formData.default_contract_pattern,
                          contract_type_tag: value === "none" ? "" : value
                        }
                      })}
                    >
                      <SelectTrigger data-testid="enrollment-type-contract-tag-select">
                        <SelectValue placeholder="Select contract type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {contractTypeTags.map((tag) => (
                          <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contract_state" className="text-xs">State</Label>
                    <Select
                      value={formData.default_contract_pattern?.state || "none"}
                      onValueChange={(value) => setFormData({
                        ...formData, 
                        default_contract_pattern: {
                          ...formData.default_contract_pattern,
                          state: value === "none" ? "" : value
                        }
                      })}
                    >
                      <SelectTrigger data-testid="enrollment-type-contract-state-select">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Any State</SelectItem>
                        {US_STATES.map((state) => (
                          <SelectItem key={state.id} value={state.id}>{state.name} ({state.id})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {formData.default_contract_pattern?.contract_type_tag && (
                  <p className="text-xs text-blue-600 mt-2">
                    System will auto-select: Active contract matching "{formData.default_contract_pattern.contract_type_tag}"
                    {formData.default_contract_pattern.state && ` in ${formData.default_contract_pattern.state}`}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>Allowed Funding Sources</Label>
              <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 border rounded">
                {fundingSources.filter(fs => fs.is_active).map((fs) => (
                  <label key={fs.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={(formData.allowed_funding_sources || []).includes(fs.id)}
                      onChange={() => handleFundingSourceToggle(fs.id)}
                      className="rounded"
                    />
                    {fs.name}
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                data-testid="enrollment-type-active-switch"
              />
              <Label>Active</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-enrollment-type-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingType ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Delete Enrollment Type
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{deletingType?.name}&quot;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete} 
              disabled={saving}
              data-testid="confirm-delete-enrollment-type-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EnrollmentTypesPage;
